import React from 'react';
import { Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import { Download as DownloadIcon, Fullscreen as FullscreenIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from 'recharts';

// Default color palette
const DEFAULT_COLORS = [
  '#8884d8', // Purple
  '#82ca9d', // Green
  '#ff7300', // Orange
  '#ff6b6b', // Red
  '#36a2eb', // Blue
  '#ffce56', // Yellow
  '#4bc0c0', // Teal
  '#9966ff', // Violet
];

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ p: 1, maxWidth: 200 }}>
        <Typography variant="caption" fontWeight="bold">
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: entry.color,
              }}
            />
            <Typography variant="caption">
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  }
  return null;
};

/**
 * DynamicChart - Renders charts from recharts-compatible specifications.
 *
 * Receives chart specs from the LLM via the RAG engine and renders
 * interactive charts using recharts.
 *
 * Props:
 *   chartSpec: {
 *     chartType: 'bar' | 'line' | 'area' | 'pie' | 'scatter' | 'composed',
 *     title: string,
 *     data: Array<object>,
 *     xAxisKey: string,
 *     series: Array<{dataKey: string, name: string, color: string, stackId?: string}>,
 *     yAxisLabel?: string,
 *     xAxisLabel?: string,
 *     stacked?: boolean,
 *     showLegend?: boolean,
 *     showGrid?: boolean,
 *     height?: number,
 *   }
 *   onExport?: () => void - Callback when export is requested
 *   compact?: boolean - Use compact styling for sidebar display
 */
function DynamicChart({ chartSpec, onExport, compact = true }) {
  if (!chartSpec || !chartSpec.data || chartSpec.data.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          No chart data available
        </Typography>
      </Box>
    );
  }

  const {
    chartType = 'bar',
    title,
    data,
    xAxisKey = 'x',
    series = [],
    yAxisLabel,
    xAxisLabel,
    stacked = false,
    showLegend = true,
    showGrid = true,
    height = compact ? 200 : 300,
  } = chartSpec;

  // Get colors for series
  const getColor = (index, seriesItem) => {
    if (seriesItem?.color) return seriesItem.color;
    return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
  };

  // Format large numbers
  const formatYAxis = (value) => {
    if (Math.abs(value) >= 1000000000) {
      return `$${(value / 1000000000).toFixed(1)}B`;
    } else if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return value;
  };

  // Common axis props
  const axisProps = {
    tick: { fontSize: compact ? 10 : 12 },
    tickLine: false,
  };

  // Render the appropriate chart type
  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
            <XAxis dataKey={xAxisKey} {...axisProps} />
            <YAxis tickFormatter={formatYAxis} {...axisProps} label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 10 } : undefined} />
            <Legend wrapperStyle={{ fontSize: compact ? 10 : 12 }} />
            <CustomTooltip />
            {series.map((s, i) => (
              <Bar
                key={s.dataKey}
                dataKey={s.dataKey}
                name={s.name || s.dataKey}
                fill={getColor(i, s)}
                stackId={stacked ? 'stack' : undefined}
                radius={[2, 2, 0, 0]}
              />
            ))}
          </BarChart>
        );

      case 'line':
        return (
          <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
            <XAxis dataKey={xAxisKey} {...axisProps} />
            <YAxis tickFormatter={formatYAxis} {...axisProps} label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 10 } : undefined} />
            <Legend wrapperStyle={{ fontSize: compact ? 10 : 12 }} />
            <CustomTooltip />
            {series.map((s, i) => (
              <Line
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.name || s.dataKey}
                stroke={getColor(i, s)}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
            <XAxis dataKey={xAxisKey} {...axisProps} />
            <YAxis tickFormatter={formatYAxis} {...axisProps} label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 10 } : undefined} />
            <Legend wrapperStyle={{ fontSize: compact ? 10 : 12 }} />
            <CustomTooltip />
            {series.map((s, i) => (
              <Area
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.name || s.dataKey}
                fill={getColor(i, s)}
                stroke={getColor(i, s)}
                fillOpacity={0.6}
                stackId={stacked ? 'stack' : undefined}
              />
            ))}
          </AreaChart>
        );

      case 'pie':
        // For pie charts, data should have name/value structure
        const pieColors = series[0]?.colors || DEFAULT_COLORS;
        return (
          <PieChart margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={compact ? 60 : 80}
              innerRadius={compact ? 30 : 40}
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
              ))}
            </Pie>
            <Legend wrapperStyle={{ fontSize: compact ? 9 : 11 }} />
            <CustomTooltip />
          </PieChart>
        );

      case 'scatter':
        return (
          <ScatterChart margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
            <XAxis dataKey={xAxisKey} name={xAxisLabel || xAxisKey} {...axisProps} />
            <YAxis tickFormatter={formatYAxis} {...axisProps} label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 10 } : undefined} />
            <Legend wrapperStyle={{ fontSize: compact ? 10 : 12 }} />
            <CustomTooltip />
            {series.map((s, i) => (
              <Scatter
                key={s.dataKey}
                name={s.name || s.dataKey}
                data={data}
                fill={getColor(i, s)}
              />
            ))}
          </ScatterChart>
        );

      case 'composed':
        // Default composed to bar + line
        return (
          <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
            <XAxis dataKey={xAxisKey} {...axisProps} />
            <YAxis tickFormatter={formatYAxis} {...axisProps} label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 10 } : undefined} />
            <Legend wrapperStyle={{ fontSize: compact ? 10 : 12 }} />
            <CustomTooltip />
            {series.map((s, i) => {
              const Component = s.type === 'line' ? Line : Bar;
              if (s.type === 'line') {
                return (
                  <Line
                    key={s.dataKey}
                    type="monotone"
                    dataKey={s.dataKey}
                    name={s.name || s.dataKey}
                    stroke={getColor(i, s)}
                    strokeWidth={2}
                  />
                );
              }
              return (
                <Bar
                  key={s.dataKey}
                  dataKey={s.dataKey}
                  name={s.name || s.dataKey}
                  fill={getColor(i, s)}
                  radius={[2, 2, 0, 0]}
                />
              );
            })}
          </BarChart>
        );

      default:
        return (
          <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
            <XAxis dataKey={xAxisKey} {...axisProps} />
            <YAxis tickFormatter={formatYAxis} {...axisProps} />
            <Legend wrapperStyle={{ fontSize: compact ? 10 : 12 }} />
            <CustomTooltip />
            {series.map((s, i) => (
              <Bar
                key={s.dataKey}
                dataKey={s.dataKey}
                name={s.name || s.dataKey}
                fill={getColor(i, s)}
              />
            ))}
          </BarChart>
        );
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1 : 2,
        bgcolor: 'white',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant={compact ? 'caption' : 'subtitle2'} fontWeight="medium" color="text.primary">
          {title}
        </Typography>
        {onExport && (
          <Tooltip title="Export chart">
            <IconButton size="small" onClick={onExport}>
              <DownloadIcon sx={{ fontSize: compact ? 14 : 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </Paper>
  );
}

export default DynamicChart;
